import { useFocusEffect } from '@react-navigation/native';
import { theme } from 'aimo-ui';
import AuthManager from 'components/auth/AuthManager';
import BGView from 'components/common/BGView';
import AccountContext from 'context/AccountContext';
import useAfterAuthenticationRedirect from 'hooks/useAfterAuthenticationRedirect';
import useHasVehicleAndCreditCard from 'hooks/useHasVehicleAndCreditCard';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Dimensions, InteractionManager, StyleSheet, View } from 'react-native';
import { useAuthStore } from 'stores/authStore';
import { useUiStore } from 'stores/uiStore';
import { isWeb } from 'utils/deviceUtils';
import WelcomeCustomerDataFailed from './WelcomeCustomerDataFailed';
import WelcomeNotSignedContent from './WelcomeNotSignedContent';

interface WelcomeRendererProps {
  onLetsGoPress?: () => void;
  loginText: string;
  setHeight: (h: number) => void;
}

const { height } = Dimensions.get('window');

const WelcomeRenderer = ({
  onLetsGoPress,
  loginText,
  setHeight,
}: WelcomeRendererProps) => {
  const { loading, error } = useContext(AccountContext);
  const { isLoggedIn } = useIsLoggedIn();
  let content;
  if (!loading && error) {
    // has token, but customer data didnt load. show error page:
    content = <WelcomeCustomerDataFailed />;
  } else if (!isLoggedIn) {
    content = (
      <WelcomeNotSignedContent
        notSignedInText={loginText}
        onLetsGoPress={onLetsGoPress}
        setHeight={setHeight}
      />
    );
  }

  return (
    <BGView noMargins paddings={{ top: 30, left: 20, right: 20 }}>
      {content}
    </BGView>
  );
};

const setRedirectAfterSuccess = (
  isInRedirectProcess: () => boolean,
  setReturnRedirect: (allowOnboarding?: boolean) => void,
  setRedirectCallback?: (allowOnboarding?: boolean) => void,
  allowOnboarding?: boolean
) => {
  if (setRedirectCallback) {
    setRedirectCallback(allowOnboarding);
  } else if (!isInRedirectProcess()) {
    setReturnRedirect(allowOnboarding);
  }
};

const calculateWrapperHeight = (
  isLoggedIn: boolean,
  fixedHeader: boolean,
  welcomeScreenContentHeight: number
) => {
  const heightCalculation = isLoggedIn
    ? theme.normalising.heightPixel(220)
    : theme.normalising.heightPixel(600);
  const headerHeight =
    isLoggedIn && !fixedHeader ? theme.normalising.heightPixel(500) : 0;
  let wrapperHeight = height - heightCalculation + headerHeight;
  if (!isLoggedIn && welcomeScreenContentHeight > wrapperHeight) {
    wrapperHeight = welcomeScreenContentHeight;
  }
  return wrapperHeight;
};

const loginActions = ({
  isLoggedIn,
  isSignUpFlowOngoing,
  redirectAfterAuthentication,
  hasData,
  navigateToNext,
  isInRedirectProcess,
  setRedirectAfterAuthentication,
}: {
  isLoggedIn: boolean;
  isSignUpFlowOngoing: boolean;
  redirectAfterAuthentication: boolean;
  hasData: boolean;
  navigateToNext: (waitForStateUpdate: boolean) => void;
  isInRedirectProcess: () => boolean;
  setRedirectAfterAuthentication: (state: boolean) => void;
}) => {
  if (isLoggedIn && !isSignUpFlowOngoing) {
    if (redirectAfterAuthentication) {
      setRedirectAfterAuthentication(false);
      if (!hasData) {
        navigateToNext(!isInRedirectProcess());
      }
    }
  }
};

const LoginStates = ({
  fixedHeader,
  loginText,
  setRedirectCallback,
}: {
  fixedHeader: boolean;
  loginText: string;
  setRedirectCallback?: (allowOnboarding?: boolean) => void;
}) => {
  // Note: on web execution signIn and signUp never resolve the promise, due to msal library
  // and we therefore use another approach in AuthManager.web.tsx
  const [welcomeScreenContentHeight, setWelcomeScreenContentHeight] =
    useState(0);
  const [redirectAfterAuthentication, setRedirectAfterAuthentication] =
    useState(false);

  const { isLoggedIn } = useIsLoggedIn();
  const { navigateToNext, isInRedirectProcess } =
    useAfterAuthenticationRedirect();
  const hasData = useHasVehicleAndCreditCard();
  const isSignUpFlowOngoing = useUiStore((state) => state.isSignUpFlow);
  const accessTokenValue = useAuthStore((state) => state.accessToken);

  const wrapperHeight = calculateWrapperHeight(
    isLoggedIn,
    fixedHeader,
    welcomeScreenContentHeight
  );

  const styles = StyleSheet.create({
    onboardingV2: {
      height: wrapperHeight,
      borderRadius: 0,
      backgroundColor: '#FFF',
    },
    onboarding: {
      height: wrapperHeight,
    },
  });

  const handleRedirectsAfterSuccess = useCallback(
    (skipRedirectCheck = false) => {
      setRedirectAfterSuccess(
        skipRedirectCheck ? () => false : isInRedirectProcess,
        navigateToNext,
        setRedirectCallback,
        true
      );
      setRedirectAfterAuthentication(true);
    },
    [setRedirectCallback, isInRedirectProcess, navigateToNext]
  );

  useEffect(() => {
    if (accessTokenValue && isLoggedIn) {
      handleRedirectsAfterSuccess();
    }
  }, [accessTokenValue, isLoggedIn, handleRedirectsAfterSuccess]);

  // If terms are not accepted redirect to approve terms
  // Or redirect to next screen after authentication
  useFocusEffect(
    React.useCallback(() => {
      const task = InteractionManager.runAfterInteractions(() => {
        loginActions({
          isLoggedIn: isLoggedIn,
          isSignUpFlowOngoing: isSignUpFlowOngoing,
          redirectAfterAuthentication: redirectAfterAuthentication,
          hasData: hasData,
          navigateToNext: navigateToNext,
          isInRedirectProcess: isInRedirectProcess,
          setRedirectAfterAuthentication: setRedirectAfterAuthentication,
        });
        return () => task.cancel();
      });
    }, [
      isLoggedIn,
      isSignUpFlowOngoing,
      redirectAfterAuthentication,
      hasData,
      navigateToNext,
      isInRedirectProcess,
      setRedirectAfterAuthentication,
    ])
  );

  const renderer = () => {
    return (
      <WelcomeRenderer
        onLetsGoPress={AuthManager.suSiAsync}
        loginText={loginText}
        setHeight={setWelcomeScreenContentHeight}
      />
    );
  };

  return isWeb ? (
    renderer()
  ) : (
    <View style={styles.onboardingV2}>{renderer()}</View>
  );
};

export default LoginStates;
